import { CoinCell } from "Infrastructure/Components/Tables/Cells/CoinCell";
import { DateCell } from "Infrastructure/Components/Tables/Cells/DateCell";
import { NameCell } from "Infrastructure/Components/Tables/Cells/NameCell";
import { PercentageCell } from "Infrastructure/Components/Tables/Cells/PercentageCell";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Box } from "@mui/material";
import { Suspense, useState } from "react";
import { useQuestQuery } from "../../../Quests/Queries/useQuestQuery";
import type { QuestDetail } from "../../Api/Api";
import { QuestDetailCard } from "../DetailCards/QuestDetailCard";
import { QuestTypeCell } from "./Cells/QuestTypeCell";
import { StatusChipCell } from "./Cells/StatusChipCell";
import { ReusableTable, type TableColumn } from "./ReusableTable";

const COMPANY_SIZE = 120; // TODO: Využiť API pre zistenie tohto čísla

export const QuestTable = () => {
  const [selectedQuest, setSelectedQuest] = useState<QuestDetail | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { t } = useResource();
  const { data: questList, isLoading, error } = useQuestQuery();

  const questData = (questList?.items || []).sort(
    (a, b) => b.questID - a.questID,
  );

  const handleRowClick = (quest: QuestDetail) => {
    setSelectedQuest(quest);
    setTimeout(() => setIsDetailOpen(true), 0);
  };

  const handleDetailClose = () => {
    setIsDetailOpen(false);
    setTimeout(() => setSelectedQuest(null), 300);
  };

  const columns: TableColumn<QuestDetail>[] = [
    {
      id: "name",
      label: t(Resources.Pages.DetailQuest.Fields.Name),
      sortable: true,
      format: (row) => <NameCell name={row.name} />,
    },
    {
      id: "state",
      label: t(Resources.Pages.DetailQuest.Fields.State),
      align: "left",
      sortable: true,
      format: (row) => <StatusChipCell state={row.state} />,
    },
    {
      id: "rewardCoins",
      label: t(Resources.Pages.DetailQuest.Fields.Reward),
      align: "right",
      sortable: true,
      format: (row) => (
        <CoinCell
          value={row.rewardCoins}
          suffix={t(Resources.Pages.CreateQuest.Fields.Reward.Suffix)}
        />
      ),
    },
    {
      id: "type",
      label: t(Resources.Pages.DetailQuest.Fields.Type),
      align: "right",
      sortable: true,
      format: (row) => <QuestTypeCell type={row.type} />,
    },
    {
      id: "currentParticipantsCount",
      label: t(Resources.Pages.DetailQuest.Fields.Active),
      align: "center",
      sortable: true,
      format: (row) => {
        const percentage = Math.round(
          (row.currentParticipantsCount / COMPANY_SIZE) * 100,
        );
        return (
          <PercentageCell
            value={row.currentParticipantsCount}
            percentage={percentage}
          />
        );
      },
    },
    {
      id: "completedParticipantsCount",
      label: t(Resources.Pages.DetailQuest.Fields.Completed),
      align: "center",
      sortable: true,
      format: (row) => {
        const percentage =
          row.currentParticipantsCount > 0
            ? Math.round(
                (row.completedParticipantsCount /
                  row.currentParticipantsCount) *
                  100,
              )
            : 0;
        return (
          <PercentageCell
            value={row.completedParticipantsCount}
            percentage={percentage}
          />
        );
      },
    },
    {
      id: "dateExpiration",
      label: t(Resources.Pages.DetailQuest.Fields.Expiration),
      align: "center",
      sortable: true,
      format: (row) => (
        <DateCell
          value={row.dateExpiration}
          fallbackText={t(Resources.Tables.Quests.NoTimeLimit)}
        />
      ),
    },
  ];

  if (isLoading) {
    return <Box>{t(Resources.Tables.Quests.Loading)}</Box>;
  }

  if (error) {
    return <Box>{t(Resources.Tables.Quests.Error)}</Box>;
  }

  return (
    <>
      <ReusableTable
        columns={columns}
        data={questData}
        onRowClick={handleRowClick}
        getRowKey={(quest) => `quest-${quest.questID}-${quest.name}`}
        maxHeight="100%"
        enableScroll={true}
        isLoading={isLoading}
      />

      <Suspense fallback={<div>{t(Resources.Common.Loading)}</div>}>
        <QuestDetailCard
          quest={selectedQuest}
          open={isDetailOpen}
          onClose={handleDetailClose}
        />
      </Suspense>
    </>
  );
};
